import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Lock, Loader2, Send, Eye, EyeOff, Coins } from 'lucide-react';
import api from '../../lib/api';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';

const EMPTY = { title: '', body: '', image_url: '', video_url: '', unlock_points: 0, is_published: true, notify: false };

export const BravxContentAdmin = () => {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const uploadVideo = async (file) => {
    if (!file) return;
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/upload/video?folder=fullbravx_content', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setForm(f => ({ ...f, video_url: r.data.url }));
      toast.success('Video subido a la nube');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al subir el video'); }
    finally { setUploadingVideo(false); }
  };

  const refresh = () => api.get('/fullbravx/admin/content').then(r => setPosts(r.data.posts || [])).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/upload/image?folder=fullbravx_content', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, image_url: r.data.url }));
      toast.success('Imagen subida');
    } catch { toast.error('Error al subir imagen'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error('El título es obligatorio');
    setSaving(true);
    try {
      if (editingId) await api.put(`/fullbravx/admin/content/${editingId}`, form);
      else await api.post('/fullbravx/admin/content', form);
      toast.success(editingId ? 'Post actualizado' : form.notify ? 'Post publicado — notificando a suscriptores por email' : 'Post creado');
      setForm(EMPTY); setEditingId(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este contenido exclusivo?')) return;
    try { await api.delete(`/fullbravx/admin/content/${id}`); refresh(); toast.success('Eliminado'); }
    catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-6" data-testid="bravx-content-admin">
      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-5 space-y-4">
        <h3 className="text-white font-bold flex items-center gap-2"><Lock size={16} className="text-[#D4AF37]" /> {editingId ? 'Editar contenido' : 'Nuevo contenido exclusivo'}</h3>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#A1A1AA]">Título *</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej: Aftermovie exclusivo — Chris Luno On Board" className="bg-[#121212] border-[#27272A] text-white" data-testid="content-title-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#A1A1AA]">Texto / cuerpo</Label>
          <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} placeholder="El contenido completo que solo ven los miembros…" className="bg-[#121212] border-[#27272A] text-white" data-testid="content-body-input" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-[#A1A1AA]">Imagen</Label>
            <div className="flex items-center gap-2">
              {form.image_url && <img src={form.image_url} alt="" className="h-10 w-16 object-cover rounded" />}
              <label className="px-3 py-2 bg-[#121212] border border-[#27272A] text-[#A1A1AA] rounded-lg text-xs cursor-pointer hover:text-white">
                {uploading ? 'Subiendo…' : form.image_url ? 'Cambiar' : 'Subir imagen'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
              </label>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[#A1A1AA]">Video</Label>
            <div className="flex items-center gap-2">
              <label className="px-3 py-2 bg-[#121212] border border-[#27272A] text-[#A1A1AA] rounded-lg text-xs cursor-pointer hover:text-white whitespace-nowrap">
                {uploadingVideo ? 'Subiendo video…' : 'Subir video (MP4, máx 100MB)'}
                <input type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={(e) => uploadVideo(e.target.files?.[0])} />
              </label>
              <Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="o pegá URL (YouTube/Vimeo/MP4)" className="bg-[#121212] border-[#27272A] text-white" />
            </div>
            {form.video_url && <p className="text-[10px] text-emerald-400 truncate">✓ {form.video_url}</p>}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs text-[#A1A1AA] flex items-center gap-1"><Coins size={11} className="text-[#D4AF37]" /> Desbloqueo por puntos (0 = solo suscriptores)</Label>
            <Input type="number" min="0" value={form.unlock_points} onChange={(e) => setForm({ ...form, unlock_points: Math.max(0, parseInt(e.target.value) || 0) })} className="bg-[#121212] border-[#27272A] text-white" data-testid="content-unlock-points-input" />
            <p className="text-[10px] text-[#52525B]">Ej: 1000 → un no-suscriptor puede desbloquearlo gastando 1000 pts BRAVX.</p>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer">
            <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} />
            Publicado
          </label>
          {!editingId && (
            <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer">
              <Switch checked={form.notify} onCheckedChange={(v) => setForm({ ...form, notify: v })} data-testid="content-notify-switch" />
              <span className="flex items-center gap-1"><Send size={11} /> Avisar por email a suscriptores</span>
            </label>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={saving} className="px-5 py-2.5 bg-[#D4AF37] text-black rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-[#F2D272]" data-testid="content-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {editingId ? 'Guardar cambios' : 'Publicar contenido'}
          </button>
          {editingId && (
            <button onClick={() => { setForm(EMPTY); setEditingId(null); }} className="px-4 py-2.5 border border-[#27272A] text-[#A1A1AA] rounded-lg text-sm">Cancelar</button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {posts.length === 0 && <p className="text-sm text-[#52525B] italic">Todavía no publicaste contenido exclusivo.</p>}
        {posts.map(p => (
          <div key={p.id} className="flex items-center gap-3 bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4" data-testid={`content-row-${p.id}`}>
            {p.image_url && <img src={p.image_url} alt="" className="w-14 h-10 rounded-lg object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm truncate">{p.title}</p>
              <p className="text-xs text-[#71717A] flex items-center gap-2 flex-wrap">
                {p.is_published ? <span className="inline-flex items-center gap-1 text-emerald-400"><Eye size={10} /> Publicado</span> : <span className="inline-flex items-center gap-1"><EyeOff size={10} /> Borrador</span>}
                <span>· {p.unlock_points > 0 ? <span className="text-[#D4AF37]">Desbloqueable por {p.unlock_points} pts</span> : 'Solo suscriptores'}</span>
                <span>· {p.unlocks_count || 0} desbloqueo(s)</span>
              </p>
            </div>
            <button onClick={() => { setForm({ title: p.title, body: p.body || '', image_url: p.image_url || '', video_url: p.video_url || '', unlock_points: p.unlock_points || 0, is_published: p.is_published !== false, notify: false }); setEditingId(p.id); }} className="text-[#71717A] hover:text-white p-2"><Pencil size={15} /></button>
            <button onClick={() => remove(p.id)} className="text-[#71717A] hover:text-red-400 p-2"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
